/**
 * PD-4 — the match-events log as a screen.
 *
 * The table is a component because three other pages embed it (MatchDetailPage
 * for both pebbles, StoryDetailPage for one, and — through a link —
 * RefinementSessionCard). This page is where a link INTO the log lands, and it
 * follows PD-2's rule exactly: the pebble id is in the PATH so the screen is
 * deep-linkable, and everything else is in the QUERY STRING so a pasted URL
 * reproduces the screen.
 *
 *   /match-events                      the global tail, newest first
 *   /match-events/:pebbleId            one pebble
 *   ?key=<node key>                    highlight and expand that node
 *   ?kind=<result kind|status>         show only those rows — how
 *                                      RefinementSessionCard opens the
 *                                      questions_ready node it is about
 *                                      without having to derive its key
 *   ?limit=N                           widen the window (route ceiling 100)
 *
 * `?key=` rather than a fragment: a fragment never reaches the router, so a
 * pasted `#node` link would silently render an unhighlighted page.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { listMatchEvents } from '../api/client';
import MatchEventsTable from '../components/matchevents/MatchEventsTable';
import type { MatchEventsLog } from '../components/matchevents/model';

export default function MatchEventsPage() {
  const { pebbleId } = useParams<{ pebbleId: string }>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const highlightKey = params.get('key');
  const filterLabel = params.get('kind');
  const limitParam = Number(params.get('limit'));
  const limit = Number.isFinite(limitParam) && limitParam > 0 ? Math.min(100, limitParam) : 50;

  const [log, setLog] = useState<MatchEventsLog | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftId, setDraftId] = useState(pebbleId ?? '');

  useEffect(() => {
    setDraftId(pebbleId ?? '');
  }, [pebbleId]);

  /** Only the LATEST request may write — the same rule ConversationPage records:
   *  a slow reload of pebble A landing after the operator moved to B would draw
   *  a complete, confident log of the wrong pebble. */
  const reqSeq = useRef(0);

  const load = useCallback((id: string | null, n: number) => {
    const seq = (reqSeq.current += 1);
    const latest = () => reqSeq.current === seq;
    setLoading(true);
    setError(null);
    listMatchEvents(id, n)
      .then((data) => {
        if (latest()) setLog(data);
      })
      .catch((e) => {
        if (latest()) {
          setLog(null);
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        if (latest()) setLoading(false);
      });
  }, []);

  // Changing pebble clears the screen FIRST, so A's rows are never drawn under
  // B's heading.
  useEffect(() => {
    setLog(null);
    setError(null);
  }, [pebbleId]);

  useEffect(() => {
    load(pebbleId ?? null, limit);
    return () => {
      reqSeq.current += 1;
    };
  }, [pebbleId, limit, load]);

  return (
    <div className="space-y-4">
      <form
        className="card flex items-end gap-3 flex-wrap"
        onSubmit={(e) => {
          e.preventDefault();
          const id = draftId.trim();
          // The URL is the state: navigating is what loads the log, which is
          // also what makes the result a link the operator can paste.
          navigate(id ? `/match-events/${encodeURIComponent(id)}` : '/match-events');
        }}
      >
        <div className="flex-1 min-w-[18rem]">
          <label htmlFor="me-pebble-id" className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
            Pebble id
          </label>
          <input
            id="me-pebble-id"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
            placeholder="pebble uuid — blank for the global tail"
            value={draftId}
            onChange={(e) => setDraftId(e.target.value)}
          />
        </div>
        <button type="submit" className="px-3 py-2 text-sm rounded bg-pebbl-600 text-white hover:bg-pebbl-700">
          Load
        </button>
        <button
          type="button"
          className="px-3 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50"
          onClick={() => load(pebbleId ?? null, limit)}
        >
          Reload
        </button>
        <span className="text-xs text-gray-500">
          {pebbleId ? `pebble ${pebbleId}` : 'global tail'} · newest {limit}
        </span>
      </form>

      {loading && <div className="text-sm text-gray-500">Loading match events…</div>}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700" role="alert">
          Error: {error}
        </div>
      )}

      {!loading && !error && (
        <MatchEventsTable
          log={log}
          highlightKey={highlightKey}
          filterLabel={filterLabel}
          onAfterReplay={() => load(pebbleId ?? null, limit)}
        />
      )}
    </div>
  );
}
